/**
 * debugReportCapture.network — bounded network-request ring buffer (HAR-lite).
 *
 * WHY (Sortie-3 follow-up, explicitly deferred in lastSaveAttemptStore.ts):
 * `lastSaveAttempts` only breadcrumbs the ≤5 most recent SL POST mutations. It
 * says nothing about the GET/asset/API traffic around a failure — the exact
 * "what was the app fetching when it broke" signal triage needs. This module
 * mirrors the console ring buffer for the network: a bounded, in-memory list of
 * recent requests sourced from the Performance Resource Timing API via a
 * `PerformanceObserver`.
 *
 * Payload-free BY DESIGN: Resource Timing exposes URL + initiator + timing +
 * transfer size — never request/response bodies or headers. URLs are redacted
 * at egress (see `debugReportRedact.redactUrl`) so query-string tokens never
 * ship. HTTP status is NOT available from Resource Timing; `lastSaveAttempts`
 * remains the status-bearing signal for SL writes and the two compose into the
 * bundle's network picture.
 *
 * Fail-open: when `PerformanceObserver` / the `resource` entry type is
 * unavailable (SSR, jsdom, old Safari) the installer no-ops and the buffer
 * stays empty — the bundle degrades gracefully, never throws.
 */

export interface NetworkEntry {
  /** Requested URL (redacted at egress). */
  url: string
  /** Resource Timing initiator ('fetch', 'xmlhttprequest', 'img', 'script', …). */
  initiatorType: string
  /** ISO timestamp the request started (timeOrigin + startTime). */
  startedAt: string
  /** Wall-clock duration in ms (rounded). */
  durationMs: number
  /** Bytes over the wire when exposed by the browser, else null. */
  transferSize: number | null
}

const networkBuffer: NetworkEntry[] = []
const MAX_NETWORK_ENTRIES = 100

/** Snapshot the current network ring buffer (most-recent-last). */
export function getNetworkBuffer(): NetworkEntry[] {
  return [...networkBuffer]
}

/** Test-only reset so a suite can assert capture from a known-empty state. */
export function __resetNetworkBufferForTest(): void {
  networkBuffer.length = 0
}

/** Append a resource timing to the ring buffer, trimming to the cap. */
function pushResourceEntry(entry: PerformanceResourceTiming, timeOrigin: number): void {
  networkBuffer.push({
    url: entry.name,
    initiatorType: entry.initiatorType || 'other',
    startedAt: new Date(timeOrigin + entry.startTime).toISOString(),
    durationMs: Math.round(entry.duration),
    transferSize: typeof entry.transferSize === 'number' ? entry.transferSize : null,
  })
  while (networkBuffer.length > MAX_NETWORK_ENTRIES) networkBuffer.shift()
}

/**
 * Install the Resource Timing observer once (idempotent). Guarded so a runtime
 * without `PerformanceObserver` (or without the `resource` entry type) simply
 * leaves the buffer empty rather than throwing.
 */
export function installNetworkCapture(): void {
  if (typeof window === 'undefined') return
  const w = window as Window & { __DEBUG_NETWORK_PATCHED__?: boolean }
  if (w.__DEBUG_NETWORK_PATCHED__) return
  if (typeof PerformanceObserver === 'undefined') return
  try {
    const timeOrigin =
      typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number'
        ? performance.timeOrigin
        : Date.now()
    const observer = new PerformanceObserver((list) => {
      for (const raw of list.getEntries()) {
        // Only resource entries carry initiatorType/transferSize; guard the cast.
        if (raw.entryType !== 'resource') continue
        pushResourceEntry(raw as PerformanceResourceTiming, timeOrigin)
      }
    })
    // `buffered: true` replays resource entries recorded before we attached,
    // so the buffer is populated even for a report opened right after load.
    observer.observe({ type: 'resource', buffered: true })
    w.__DEBUG_NETWORK_PATCHED__ = true
  } catch {
    // `resource` entry type unsupported on this runtime — degrade to empty.
  }
}
